import * as React from "react";
import type moment from "moment";
import type { ModifiersShape } from "react-dates";
import { cn } from "./cn";
import {
  isHoveredAndInRange,
  isOutOfRange,
  isSelected,
  shouldHaveLeftPill,
  shouldHaveRightPill,
} from "./dayModifiers";

export interface TypeCalendarDayProps {
  modifiers: ModifiersShape;
  day: moment.Moment;
  children?: React.ReactNode;
  className?: string;
}

/**
 * Static day cell. Renders the semantic `seeds-datepicker-day*` classes from
 * datepicker.css with no runtime CSS-in-JS.
 *
 * The legacy cell was `styled(Box)`, and Box ships no CSS of its own, so
 * `.seeds-datepicker-day` reproduces Box's baseline (box-sizing + font-family)
 * and this component reproduces Box's forwardRef.
 *
 * The visual states are mutually exclusive, exactly as in the styled version's
 * if / else-if chain: a selected day never also picks up the hovered or
 * out-of-range treatment. Pill edges are modifier classes rather than values,
 * so an absent edge emits no declaration at all.
 */
const dayStateClasses = (
  modifiers: ModifiersShape,
  day: moment.Moment
): Record<string, boolean> => {
  if (isSelected(modifiers)) {
    return {
      "seeds-datepicker-day-selected": true,
      "seeds-datepicker-day-pill-left": shouldHaveLeftPill(modifiers, day),
      "seeds-datepicker-day-pill-right": shouldHaveRightPill(modifiers, day),
    };
  }

  if (isHoveredAndInRange(modifiers)) {
    return { "seeds-datepicker-day-hovered": true };
  }

  if (isOutOfRange(modifiers)) {
    return { "seeds-datepicker-day-out-of-range": true };
  }

  return {};
};

const CalendarDayTailwind = React.forwardRef<
  HTMLDivElement,
  TypeCalendarDayProps
>(({ modifiers, day, className, children }, ref) => (
  <div
    ref={ref}
    // Semantic classes come before the consumer className so consumer utilities
    // can override the layered base styles.
    className={cn(
      "seeds-datepicker-day",
      dayStateClasses(modifiers, day),
      className
    )}
  >
    {children}
  </div>
));

CalendarDayTailwind.displayName = "CalendarDay";

export default CalendarDayTailwind;
